import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { AlertService } from 'app/services/alert.service';
import { LoggerService } from 'app/services/logger.service';
import { catchError, map, of, switchMap, tap } from 'rxjs';
import { SalesRepService } from '../services/sales-rep.service';

import * as SalesRepActions from './sales-rep.actions';

@Injectable()
export class SalesRepEffects {
    private _actions$ = inject(Actions);
    private _store = inject(Store);
    private logger = inject(LoggerService);
    private _salesRepService = inject(SalesRepService);
    private _alertService = inject(AlertService);

    messages = {
        getAllError: 'Error getting all sales rep',
        conectionError: 'Error connecting to the server',
        unknownError: 'Unknown error',
    };

    // -----------------------------------------------------------
    // @ Effects
    // -----------------------------------------------------------

    // Get All Sales Rep
    getAllSalesRep$ = createEffect(() =>
        this._actions$.pipe(
            ofType(SalesRepActions.loadSalesReps),
            switchMap(() => {
                return this._salesRepService.getAll().pipe(
                    // tap((res) => this.logger.log('response', res)),  // Debug
                    map((res) => {
                        return SalesRepActions.loadSalesRepsSuccess({
                            salesReps: res.data,
                        });
                    }),
                    catchError((error) =>
                        of(
                            SalesRepActions.loadSalesRepsFailure({
                                error: error?.error?.message || 'Unknown error',
                            })
                        )
                    )
                );
            })
        )
    );

    // Get All VIP Upgrades Sales Rep
    getAllVIPUpgradesSalesRep$ = createEffect(() =>
        this._actions$.pipe(
            ofType(SalesRepActions.loadVIPUpgradesSalesReps),
            switchMap(() => {
                return this._salesRepService.getVIPUpgrades().pipe(
                    // tap((res) => this.logger.log('response', res)), // Debug
                    map((res) => {
                        return SalesRepActions.loadSalesRepsSuccess({
                            salesReps: res.data,
                        });
                    }),
                    catchError((error) =>
                        of(
                            SalesRepActions.loadSalesRepsFailure({
                                error: error?.error?.message || 'Unknown error',
                            })
                        )
                    )
                );
            })
        )
    );

    // Get All VIP Upgrades Sales Rep Without Sales
    getAllVIPUpgradesSalesRepWithoutSales$ = createEffect(() =>
        this._actions$.pipe(
            ofType(SalesRepActions.loadVIPUpgradesSalesRepsWithoutSales),
            switchMap((action) => {
                return this._salesRepService
                    .getVIPUpgradesWithoutSales(action.playdateId)
                    .pipe(
                        // tap((res) => this.logger.log('res', res)), // Debug
                        map((res) => {
                            return SalesRepActions.loadSalesRepsSuccess({
                                salesReps: res.data,
                            });
                        }),
                        catchError((error) =>
                            of(
                                SalesRepActions.loadSalesRepsFailure({
                                    error:
                                        error?.error?.message ||
                                        'Unknown error',
                                })
                            )
                        )
                    );
            })
        )
    );

    // Get All Midway Sales Rep
    getAllMidwaySalesRep$ = createEffect(() =>
        this._actions$.pipe(
            ofType(SalesRepActions.loadMidwaySalesReps),
            switchMap(() => {
                return this._salesRepService.getMidway().pipe(
                    // tap((res) => this.logger.log('res', res)), // Debug
                    map((res) => {
                        return SalesRepActions.loadSalesRepsSuccess({
                            salesReps: res.data,
                        });
                    }),
                    catchError((error) =>
                        of(
                            SalesRepActions.loadSalesRepsFailure({
                                error: error?.error?.message || 'Unknown error',
                            })
                        )
                    )
                );
            })
        )
    );

    // Get All Midway Sales Rep Without Sales
    getAllMidwaySalesRepWithoutSales$ = createEffect(() =>
        this._actions$.pipe(
            ofType(SalesRepActions.loadMidwaySalesRepsWithoutSales),
            switchMap((action) => {
                return this._salesRepService
                    .getMidwayWithoutSales(action.playdateId)
                    .pipe(
                        // tap((res) => this.logger.log('res', res)), // Debug
                        map((res) => {
                            return SalesRepActions.loadSalesRepsSuccess({
                                salesReps: res.data,
                            });
                        }),
                        catchError((error) =>
                            of(
                                SalesRepActions.loadSalesRepsFailure({
                                    error:
                                        error?.error?.message ||
                                        'Unknown error',
                                })
                            )
                        )
                    );
            })
        )
    );

    // Get All Concession Sales Rep
    getAllConcessionSalesRep$ = createEffect(() =>
        this._actions$.pipe(
            ofType(SalesRepActions.loadConcessionSalesReps),
            switchMap(() => {
                return this._salesRepService.getConcession().pipe(
                    // tap((res) => this.logger.log('res', res)), // Debug
                    map((res) => {
                        return SalesRepActions.loadSalesRepsSuccess({
                            salesReps: res.data,
                        });
                    }),
                    catchError((error) =>
                        of(
                            SalesRepActions.loadSalesRepsFailure({
                                error: error?.error?.message || 'Unknown error',
                            })
                        )
                    )
                );
            })
        )
    );

    // Get All Concession Sales Rep Without Sales
    getAllConcessionSalesRepWithoutSales$ = createEffect(() =>
        this._actions$.pipe(
            ofType(SalesRepActions.loadConcessionSalesRepsWithoutSales),
            switchMap((action) => {
                return this._salesRepService
                    .getConcessionWithoutSales(action.playdateId)
                    .pipe(
                        // tap((res) => this.logger.log('res', res)), // Debug
                        map((res) => {
                            return SalesRepActions.loadSalesRepsSuccess({
                                salesReps: res.data,
                            });
                        }),
                        catchError((error) =>
                            of(
                                SalesRepActions.loadSalesRepsFailure({
                                    error:
                                        error?.error?.message ||
                                        'Unknown error',
                                })
                            )
                        )
                    );
            })
        )
    );

    // Get All Parking Sales Rep
    getAllParkingSalesRep$ = createEffect(() =>
        this._actions$.pipe(
            ofType(SalesRepActions.loadParkingSalesReps),
            switchMap(() => {
                return this._salesRepService.getParking().pipe(
                    // tap((res) => this.logger.log('res', res)), // Debug
                    map((res) => {
                        return SalesRepActions.loadSalesRepsSuccess({
                            salesReps: res.data,
                        });
                    }),
                    catchError((error) =>
                        of(
                            SalesRepActions.loadSalesRepsFailure({
                                error: error?.error?.message || 'Unknown error',
                            })
                        )
                    )
                );
            })
        )
    );

    // Get All Parking Sales Rep Without Sales
    getAllParkingSalesRepWithoutSales$ = createEffect(() =>
        this._actions$.pipe(
            ofType(SalesRepActions.loadParkingSalesRepsWithoutSales),
            switchMap((action) => {
                return this._salesRepService
                    .getParkingWithoutSales(action.playdateId)
                    .pipe(
                        // tap((res) => this.logger.log('res', res)), // Debug
                        map((res) => {
                            return SalesRepActions.loadSalesRepsSuccess({
                                salesReps: res.data,
                            });
                        }),
                        catchError((error) =>
                            of(
                                SalesRepActions.loadSalesRepsFailure({
                                    error:
                                        error?.error?.message ||
                                        'Unknown error',
                                })
                            )
                        )
                    );
            })
        )
    );

    // Get All Others Sales Rep
    getAllOthersSalesRep$ = createEffect(() =>
        this._actions$.pipe(
            ofType(SalesRepActions.loadOthersSalesReps),
            switchMap(() => {
                return this._salesRepService.getOthers().pipe(
                    // tap((res) => this.logger.log('res', res)), // Debug
                    map((res) => {
                        return SalesRepActions.loadSalesRepsSuccess({
                            salesReps: res.data,
                        });
                    }),
                    catchError((error) =>
                        of(
                            SalesRepActions.loadSalesRepsFailure({
                                error: error?.error?.message || 'Unknown error',
                            })
                        )
                    )
                );
            })
        )
    );

    // Get All Others Sales Rep Without Sales
    getAllOthersSalesRepWithoutSales$ = createEffect(() =>
        this._actions$.pipe(
            ofType(SalesRepActions.loadOthersSalesRepsWithoutSales),
            switchMap((action) => {
                return this._salesRepService
                    .getOthersWithoutSales(action.playdateId)
                    .pipe(
                        // tap((res) => this.logger.log('res', res)), // Debug
                        map((res) => {
                            return SalesRepActions.loadSalesRepsSuccess({
                                salesReps: res.data,
                            });
                        }),
                        catchError((error) =>
                            of(
                                SalesRepActions.loadSalesRepsFailure({
                                    error:
                                        error?.error?.message ||
                                        'Unknown error',
                                })
                            )
                        )
                    );
            })
        )
    );

    // Get Sales Rep By Location Type
    getSalesRepByLocationType$ = createEffect(() =>
        this._actions$.pipe(
            ofType(SalesRepActions.loadSalesRepsByLocationType),
            switchMap((action) => {
                return this._salesRepService
                    .getByLocationType(action.locationTypeId)
                    .pipe(
                        // tap((res) => this.logger.log('res', res)), // Debug
                        map((res) => {
                            return SalesRepActions.loadSalesRepsSuccess({
                                salesReps: res.data,
                            });
                        }),
                        catchError((error) =>
                            of(
                                SalesRepActions.loadSalesRepsFailure({
                                    error:
                                        error?.error?.message ||
                                        'Unknown error',
                                })
                            )
                        )
                    );
            })
        )
    );

    // Get One Sales Rep
    getSalesRep$ = createEffect(() =>
        this._actions$.pipe(
            ofType(SalesRepActions.loadSalesRep),
            switchMap((action) => {
                return this._salesRepService.getOne(action.id).pipe(
                    // tap((res) => this.logger.log('res', res)), // Debug
                    map((res) => {
                        return SalesRepActions.loadSalesRepSuccess({
                            salesRep: res.data,
                        });
                    }),
                    catchError((error) =>
                        of(
                            SalesRepActions.loadSalesRepFailure({
                                error: error?.error?.message || 'Unknown error',
                            })
                        )
                    )
                );
            })
        )
    );

    // Create SalesRep
    createSalesRep$ = createEffect(() =>
        this._actions$.pipe(
            ofType(SalesRepActions.createSalesRep),
            switchMap((action) => {
                return this._salesRepService.create(action.data).pipe(
                    // tap((res) => this.logger.log('res', res)), // Debug
                    map((res) => {
                        return SalesRepActions.createSalesRepSuccess({
                            salesRep: res.data,
                            success: 'Sales Rep. created successfully',
                        });
                    }),
                    catchError((error) =>
                        of(
                            SalesRepActions.createSalesRepFailure({
                                error: error?.error?.message || 'Unknown error',
                            })
                        )
                    )
                );
            })
        )
    );

    // Update SalesRep
    updateSalesRep$ = createEffect(() =>
        this._actions$.pipe(
            ofType(SalesRepActions.updateSalesRep),
            switchMap((action) => {
                return this._salesRepService
                    .update(action.data, action.id)
                    .pipe(
                        // tap((res) => this.logger.log('res', res)), // Debug
                        map((res) => {
                            return SalesRepActions.updateSalesRepSuccess({
                                salesRep: res.data,
                                success: 'Sales Rep. updated successfully',
                            });
                        }),
                        catchError((error) =>
                            of(
                                SalesRepActions.updateSalesRepFailure({
                                    error:
                                        error?.error?.message ||
                                        'Unknown error',
                                })
                            )
                        )
                    );
            })
        )
    );

    // Delete SalesRep
    deleteSalesRep$ = createEffect(() =>
        this._actions$.pipe(
            ofType(SalesRepActions.deleteSalesRep),
            switchMap((action) => {
                return this._salesRepService.delete(action.id).pipe(
                    // tap((res) => this.logger.log('res', res)), // Debug
                    map(() => {
                        return SalesRepActions.deleteSalesRepSuccess({
                            id: action.id,
                            success: 'Sales Rep deleted successfully',
                        });
                    }),
                    catchError((error) =>
                        of(
                            SalesRepActions.deleteSalesRepFailure({
                                error: error?.error?.message || 'Unknown error',
                            })
                        )
                    )
                );
            })
        )
    );

    // -----------------------------------------------------------
    // @ Alerts
    // -----------------------------------------------------------

    onSalesRepActionSuccess$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    // SalesRepActions.loadSalesRepsSuccess,
                    // SalesRepActions.loadSalesRepSuccess
                    SalesRepActions.createSalesRepSuccess,
                    SalesRepActions.updateSalesRepSuccess,
                    SalesRepActions.deleteSalesRepSuccess
                ),
                tap((action) => {
                    this._alertService.showAlert(action.success, 'success');
                })
            ),
        { dispatch: false }
    );

    onSalesRepActionFailure$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    SalesRepActions.loadSalesRepsFailure,
                    SalesRepActions.loadSalesRepFailure,
                    SalesRepActions.createSalesRepFailure,
                    SalesRepActions.updateSalesRepFailure,
                    SalesRepActions.deleteSalesRepFailure
                ),
                tap((action) => {
                    this._alertService.showAlert(action.error, 'error');
                })
            ),
        { dispatch: false }
    );
}
